// Bicycle AI API Types

import { EntityPropertyValue, LogicalOperator } from "../../../core";
import { OnboardCopilotResponse } from "../add-usecase/types";
import { MappingWorkflowStatus, UserServiceMappingRuleResponse } from "../auto-discovery/types";
import { ChatConversation, LowLevelFragment, QuestionFragment } from "../chat";
import { CompareOperator } from "../operationalise";
import { EntityMasterMapping } from "../entity-mapping/types";
import { KnowledgeBaseSettings, KnowledgeSummary, UseCaseConfig, VerticalIdentifier } from "../use-case-catalog";

export type WorkflowRunRequest = {
  pollId: string;
  mappingRequest: MappingRequest;
  workflowType: "MAPPING_WORKFLOW";
  globalInformation?: any;
  synchronous?: boolean;
  knowledgeSummary?: KnowledgeSummary;
};

export type MappingRequest = {
  usecaseId: string;
  connectionIds: string[]; // data sources to create mapping rules
  contextConfigs: KnowledgeBaseSettings[]; // data to be sent to context engine
  verticalIdentifier: VerticalIdentifier;
  useCaseRecommendationParams: UseCaseRecommendationParams;
};

export type UseCaseRecommendationParams = {
  companyName?: string;
  categoryCount?: number;
  useCaseCount?: number;
  prompt?: string;
  focus: string;
  group: string;
  role: string;
};

export interface MappingWorkflowStep {
  stepName: string;
  status: MappingWorkflowStatus;
  errorMessage: string;
}

export type UseCaseRecommendationInterrupt = {
  recommendedUseCaseConfigs: UseCaseConfig[];
  fragment: LowLevelFragment;
};

export type UseCaseConfigInterrupt = {
  useCaseConfig: UseCaseConfig;
  fragment: LowLevelFragment;
};

export type EventTimeStampField = {
  format: string;
  timezone: string;
  fieldName: string;
};

export type EventTimeStampInfo = {
  eventTimeStampFields: EventTimeStampField[];
  currentStartTimeMicrosField?: EventTimeStampField;
  ruleId: string;
};

export type KnowledgeSummaryInterrupt = {
  knowledgeSummaryConfirmation: boolean;
  eventTimeStampInfos: EventTimeStampInfo[];
};

export type MappingResponse = {
  mappingWorkflowSteps: MappingWorkflowStep[];
  eventMappingResponse: GetUserServiceMappingRulesResponses;
  questionFragments: QuestionFragment[];
  onboardCopilotResponse: OnboardCopilotResponse;
  conversation: ChatConversation;
  recommendedUseCaseConfigs?: UseCaseConfig[];
  ingestionJobId: string;
  mappingRuleIds: string[];

  // oneOf interrupt
  useCaseRecommendationInterrupt?: UseCaseRecommendationInterrupt;
  useCaseConfigInterrupt?: UseCaseConfigInterrupt;
  knowledgeSummaryInterrupt?: KnowledgeSummaryInterrupt;
};

export type MappingWorkflowV2Output = {
  pollId: string;
  status: MappingWorkflowStatus;
  mappingResponse: MappingResponse;
};

export type UseCaseRecommendationInterruptResponse = {
  recommendedUseCaseConfig?: UseCaseConfig;
  recommendedUseCaseName?: string;
  usecaseRecommendationParams?: UseCaseRecommendationParams;
};

export type KnowledgeSummaryInterruptResponse = {
  mappingRulesResponse: UserServiceMappingRuleResponse[];
};

export type GetUserServiceMappingRulesResponses = {
  responses: UserServiceMappingRuleResponse[];
};

export enum RuleType {
  ENTITY = "ENTITY",
  EVENT = "EVENT"
}

export type GetRuleInfoRequest = {
  pollId: string;
  ruleIds: string[];
  type?: RuleType;
};

export type GetEntityTypeInfoResponse = {
  entityMappingResponses: Record<string, EntityMappingInfo>;
};

export type EntityMappingInfo = {
  entityMasterMapping: EntityMasterMapping;
  groupId: string;
  groupName: string;
  connectionIds: string[];
  regexId: string;
  streamIds: string[];
};

export type WorkflowResumeRequest = {
  pollId: string;
  useCaseRecommendationInterruptResponse?: UseCaseRecommendationInterruptResponse;
  knowledgeSummaryInterruptResponse?: KnowledgeSummaryInterruptResponse;
};

export interface GroupRolesMap {
  group_name: string;
  roles: string[];
}

export interface FocusGroupsMap {
  focus_name: string;
  groups: GroupRolesMap[];
}

export interface UseCaseUserInfoRequest {
  focus: string;
  group: string;
  role: string;

  generateFullHierarchy?: boolean;
}

export interface UseCaseUserInfoResponse {
  hierarchy: FocusGroupsMap[];
}

export enum WorkflowType {
  USE_CASE_RECOMMENDATION = "USE_CASE_RECOMMENDATION"
}

export interface UseCaseRecommendationPayload {
  companyName?: string;
  categoryCount?: number;
  useCaseCount?: number;
  focus?: string;
  group?: string;
  role?: string;
  prompt?: string;
  isExplorationAllowed?: boolean;
}

export interface UseCaseRecommendationRequest {
  workflowType: WorkflowType;
  useCaseRecommendationRequest: UseCaseRecommendationPayload;
}

export interface UseCaseRecommendationResponse {
  recommendedUseCaseConfigs: UseCaseConfig[];
}

export interface PeopleBusinessInfoRequest {
  linkedinProfileUrl?: string;
  businessEmail?: string;
  enrichRealTime: boolean;
  provideUserBusinessInfo: boolean;
  provideFullHierarchy?: boolean;
}

export interface PeopleBusinessInfoResponse {
  userBusinessInfoResponse: UseCaseUserInfoResponse;
  peopleBusinessInfo: string;
  savedToStore: boolean;
} // Adjust import path as needed

export interface DecisionTree {
  id: string;
  name: string;
  startNodeIds: string[]; // The entry points into the graph
  nodes: Record<string, ExecutionNode>; // A map of all nodes in the graph
}

export interface ExecutionNode {
  nodeId: string;
  cause?: Cause;
  action?: Action;
  driver?: Driver;
  pointers?: NextNodePointers;
}

export interface NextNodePointers {
  onTrueNodeIds?: string[];
  onFalseNodeIds?: string[];
  onCompleteNodeIds?: string[];
}

export interface Driver {
  id: string;
  name: string;
  description: string;
  predicate?: DriverPredicate; // The aggregation logic (ANY_ONE, ALL_MATCH)
  childCauseNodeIds?: string[]; // Pointers to the Cause nodes this Driver manages
  faIcon?: string;
  metadata?: Record<string, string>;
}

export interface Cause {
  id: string;
  name: string;
  description: string;
  causeType?: string;
  predicate?: CausePredicate; // The predicate to evaluate the tool's output
  toolReference?: ToolReference;
  inputParameters?: Record<string, EntityPropertyValue>;
  faIcon?: string;
  metadata?: Record<string, string>;
}

export interface Action {
  id: string;
  name: string;
  description: string;
  toolReference?: ToolReference;
  parameters?: Record<string, EntityPropertyValue>;
  faIcon?: string;
  metadata?: Record<string, string>;
  sourceTypeId?: string;
}

export interface ActionList {
  actions: Action[];
}

export interface DriverList {
  drivers: Driver[];
}

export interface DriverPredicate {
  predicateType: DriverPredicateType;
  description?: string;
}

export enum DriverPredicateType {
  ANY_ONE = "ANY_ONE",
  ALL_MATCH = "ALL_MATCH"
}

export interface CausePredicate {
  description?: string;
  expression?: {
    simple?: SimpleExpression;
    compound?: CompoundExpression;
  };
}

export interface SimpleExpression {
  leftOperand?: Operand;
  operator?: CompareOperator;
  rightOperand?: Operand; // Ignored for unary operators like 'exists' or 'isNull'
}

export interface CompoundExpression {
  operator?: LogicalOperator;
  predicates?: CausePredicate[]; // For AND/OR: 2+ items, For NOT: exactly 1 item
}

export interface Operand {
  valueSource?: {
    variableName?: string; // e.g., "promo_count"
    literalValue?: EntityPropertyValue; // A literal/scalar value
  };
}

export interface ToolReference {
  tool?: {
    toolId?: string;
    //toolConfig?: Tool;
  };
  parameters?: Record<string, EntityPropertyValue>;
}

export interface DecisionTreeWorkflow {
  decisionTreeWorkflow?: {
    decisionTreeId?: string;
    decisionTree?: DecisionTree;
  };
}

export type NodePayloadType = "cause" | "action" | "driver";
